import {
  Cluster,
  ClusterStats,
  MarkerClusterer,
  onClusterClickHandler,
  Renderer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import { useCallback, useEffect, useMemo, useRef } from "react";
import ReactDomServer from "react-dom/server";
import {
  MapSearchData,
  MarkerDataSystem,
} from "api/interfaces/dashboardInterface.interface";
import DefaultMarker from "pages/dashboard/map/sub/markers/DefaultMarker";
import {
  CustomMarker,
  MarkerDataType,
} from "pages/dashboard/map/GoogleMapContainer";
import { useMediaQuery } from "react-responsive";
import * as mediaQuery from "components/MediaQuery";
import ClusterMarker from "pages/dashboard/map/sub/markers/ClusterMarker";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "styled-components";
import HoverInfoView from "pages/dashboard/map/sub/infoView/HoverInfoView";
import { Theme } from "redux/reducers/theme/themeReducer";
import { useAppSelector } from "redux/hooks";
import { dark, light } from "styles/theme";
import { MapFilter } from "redux/reducers/map/mapFilter";
import { getCalculateAlertStatus } from "utils/DashboardUtil";
import {
  mapSearchDataSelector,
  markerDataSelector,
} from "redux/stomp/stompMapSlice";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";

export interface Type {
  map: google.maps.Map | undefined;
  clusterRadius: number;
  handleActiveMarker: (marker: CustomMarker) => void;
  onClickMarkerAlert: (systems: MarkerDataSystem[]) => void;
  animatedRecorders: string[];
}

//BUG [RND-305] MouseOve시에 여백에 따라 팝업 위치 변경.
const calculateInfoPosition = (
  mouseEvent: MouseEvent,
  tempMarker: google.maps.marker.AdvancedMarkerElement
) => {
  const screenX = window.innerWidth;
  const screenY = window.innerHeight;
  const markerX = mouseEvent.clientX;
  const markerY = mouseEvent.clientY;

  const rightPad = markerX;
  const leftPad = screenX - markerX;

  const topPad = markerY;
  const bottomPad = screenY - markerY;

  let pos = new google.maps.Size(0, -25);

  const recorders: MarkerDataSystem[] | undefined = (tempMarker as any)
    .recorder;

  const isMulti =
    recorders !== undefined && recorders?.length !== undefined
      ? recorders?.length
      : 2;

  const singleSizeW = rightPad > leftPad ? -100 : 100;
  const multiSizeW = rightPad > leftPad ? -125 : 125;

  if (topPad > bottomPad) {
    pos = new google.maps.Size(100 * (rightPad > leftPad ? -1 : 1), -25); //close
  } else {
    if (isMulti <= 1) {
      pos = new google.maps.Size(singleSizeW, 200);
    } else {
      const calcHeight = 20 + (isMulti - 2) * 20;
      pos = new google.maps.Size(
        multiSizeW,
        calcHeight > 200 ? 200 : calcHeight
      );
    }
  }

  return pos;
};

const stringifyForDiff = (d: MarkerDataType): string => {
  const diffGrouping = d.groupingSystems
    .map((sys) => {
      return {
        recorderId: sys.recorderId,
        isAssignee: sys.isAssignee,
        recorderName: sys.name,
      };
    })
    .sort((a, b) => a.recorderId.localeCompare(b.recorderId));

  return JSON.stringify({
    recorderIds: d.recorderIds,
    mergedSystemId: d.mergedSystemId,
    latitude: d.latitude,
    longitude: d.longitude,
    alertStatusCount: d.alertStatusCount,
    groupingSystems: diffGrouping,
    assigneeEmail: d.isAssignee?.assigneeEmail,
  });
};
const samePos = (a: google.maps.LatLngLiteral, b: google.maps.LatLngLiteral) =>
  Math.abs(a.lat - b.lat) < 1e-8 && Math.abs(a.lng - b.lng) < 1e-8;
export function useAdvancedMarkers({
  map,
  clusterRadius,
  handleActiveMarker,
  onClickMarkerAlert,
  animatedRecorders,
}: Type) {
  const theme: Theme = useAppSelector((state) => state.theme);

  const markers: MarkerDataType[] = useAppSelector(markerDataSelector);
  const mapFilter: MapFilter = useAppSelector((state) => state.mapFilter);

  const markerMapRef = useRef<Record<string, CustomMarker>>({});
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const clusterMarkersRef = useRef<CustomMarker[]>([]);
  const clusterRadiusRef = useRef<number>(clusterRadius);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const infoAnchorRef = useRef<CustomMarker | null>(null);

  const prevAnimatedRef = useRef<string[]>(animatedRecorders);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const canHover = useMediaQuery({
    query: "(any-hover: hover), (hover: hover), (any-pointer: fine)",
  });
  const isMobile = mediaQuery.useIsMobile();
  const currentMapSearchData: MapSearchData[] = useAppSelector(
    mapSearchDataSelector
  );

  const defaultOnClusterClickHandler: onClusterClickHandler = (
    _: google.maps.MapMouseEvent,
    cluster: Cluster,
    map: google.maps.Map
  ): void => {
    if (cluster.bounds !== undefined) {
      map?.fitBounds(cluster.bounds, 80);
    }
  };

  const createMarkerContent = (data: MarkerDataType) => {
    const tempMarkerContent = ReactDomServer.renderToString(
      <DefaultMarker {...data} />
    );
    const tempPin = document.createElement("div");
    tempPin.innerHTML = tempMarkerContent;
    return tempPin;
  };

  const clusterRenderer: Renderer = useMemo(() => {
    return {
      render: function (cluster: Cluster, stats: ClusterStats) {
        // let ids: string = "";

        const { totalRecorder, calculateAlertStatus, recorderIds } =
          getCalculateAlertStatus(cluster.markers as CustomMarker[]);

        const tempClusterContent = ReactDomServer.renderToString(
          <ClusterMarker
            {...calculateAlertStatus}
            totalRecorder={totalRecorder}
            animatedRecorders={animatedRecorders}
          />
        );
        const tempClusterPin = document.createElement("div");
        tempClusterPin.setAttribute("data-cluster-id", recorderIds);
        tempClusterPin.innerHTML = tempClusterContent;
        const circle = tempClusterPin.querySelector(".clusterer-circle-bg");

        const tempClusterMarker = new google.maps.marker.AdvancedMarkerElement({
          map,
          position: cluster.position,
          content: tempClusterPin,
          zIndex: 5,
        });

        if (canHover && !isMobile) {
          tempClusterPin.addEventListener("mouseover", () => {
            if (circle) {
              circle.classList.add("circle-animation");
            }
            if (infoWindowRef.current) infoWindowRef.current.close();
            const clusterInfoDiv = document.createElement("div");

            const root = createRoot(clusterInfoDiv);
            root.render(
              <ThemeProvider theme={theme.theme === "light" ? light : dark}>
                <HoverInfoView
                  theme={theme}
                  count={totalRecorder}
                  calculateAlertStatus={calculateAlertStatus}
                  assignedFilter={mapFilter.filters.status.assigned}
                  isEnabled={selectedAccount.isEnabled}
                />
              </ThemeProvider>
            );

            infoWindowRef.current = new google.maps.InfoWindow({
              pixelOffset: new google.maps.Size(0, -30),
              content: clusterInfoDiv,
            });
            infoWindowRef.current.open({
              anchor: tempClusterMarker,
              map: map,
            });
          });

          tempClusterPin.addEventListener("mouseout", () => {
            if (circle) {
              circle.classList.remove("circle-animation");
            }
            infoWindowRef.current?.close();
          });
        }

        return tempClusterMarker;
      },
    };
  }, [map, infoWindowRef.current, animatedRecorders]);

  useEffect(() => {
    // console.log("console 11", markers);
    if (!map || !markers) return;

    if (markers.length === 0) {
      Object.values(markerMapRef.current).forEach((marker) => {
        marker.map = null;
      });
      markerMapRef.current = {};
      return;
    }

    const existingMarkers = markerMapRef.current;

    // 최신 id 집합
    const newMarkers = new Set(markers.map((m) => m.recorderIds));

    // 1) 삭제: 최신에 없는 id만 제거
    for (const [id, mk] of Object.entries(existingMarkers)) {
      if (!newMarkers.has(id)) {
        mk.map = null;
        delete existingMarkers[id];
      }
    }

    markers.forEach((data: MarkerDataType) => {
      if (
        !data ||
        data.latitude === undefined ||
        data.longitude === undefined
      ) {
        return;
      }

      const id = data.recorderIds;
      const position: google.maps.LatLngLiteral = {
        lat: data.latitude,
        lng: data.longitude,
      };
      const title =
        data.groupingSystems && data.groupingSystems.length > 1
          ? `${data.groupingSystems.length} SYS`
          : data.title;
      const diff = stringifyForDiff(data);
      let marker = existingMarkers[id];

      // console.log(diff);

      if (!marker) {
        const content = createMarkerContent(data);
        const circle = content.querySelector(".marker-circle-bg");

        marker = new google.maps.marker.AdvancedMarkerElement({
          position,
          content,
          title,
        });

        (marker as CustomMarker).data = data;
        (marker as any)._diff = diff;
        marker.map = map;

        marker.addEventListener(
          canHover && !isMobile ? "mouseover" : "click",
          (e: MouseEvent) => {
            if (canHover && circle) {
              circle.classList.add("circle-animation");
            }
            closeInfoWindow();
            const pos = calculateInfoPosition(e, marker);
            (marker as any).popupPosition = pos;
            marker.zIndex = 9999;
            handleActiveMarker(marker as CustomMarker);
          }
        );

        if (canHover) {
          marker.addListener("click", () => {
            // 1개의 시스템인 marker 를 클릭했을 때 왼쪽 Alert View 보여주기 위함
            onClickMarkerAlert(data.groupingSystems);
          });
          if (!isMobile) {
            marker.addEventListener("mouseout", () => {
              marker.zIndex = 1;
              if (circle) {
                circle.classList.remove("circle-animation");
              }
            });
          }
        }

        existingMarkers[id] = marker;
      }

      // 업데이트: “모든 필드 변경이 감지 대상”
      const prevFP = (marker as any)._diff as string;

      if (prevFP !== diff) {
        // 위치/타이틀 변경
        const curPos = marker.position as google.maps.LatLngLiteral;
        if (!samePos(curPos, position)) {
          marker.position = position;
        }
        if (marker.title !== title) {
          marker.title = title;
        }
        // rerendering
        marker.content = createMarkerContent(data);

        (marker as any)._diff = diff;
        marker.data = data;

        if (clustererRef.current) {
          const current = clustererRef.current;
          const allMarkers = Object.values(markerMapRef.current);

          current.clearMarkers();
          current.addMarkers(allMarkers);
        }

        if (infoWindowRef.current && infoAnchorRef.current === marker) {
          handleActiveMarker(marker as CustomMarker);
        }
      }
    });
  }, [map, markers]);

  useEffect(() => {
    if (!map || Object.values(markerMapRef.current) === undefined) return;

    const newMarkers = Object.values(markerMapRef.current);

    if (newMarkers.length === 0) {
      clustererRef.current?.clearMarkers();
      clustererRef.current = null;
      clusterMarkersRef.current = [];
    }

    const clusterer = clustererRef.current;
    const currentRadius = clusterRadius;
    const radiusChanged = clusterRadiusRef.current !== currentRadius;

    if (!clusterer || radiusChanged) {
      clustererRef.current?.clearMarkers();

      clustererRef.current = new MarkerClusterer({
        map,
        markers: newMarkers,
        algorithm: new SuperClusterAlgorithm({
          radius: clusterRadius,
        }),
        renderer: clusterRenderer,
        onClusterClick: defaultOnClusterClickHandler,
      });

      clusterRadiusRef.current = currentRadius;
      clusterMarkersRef.current = newMarkers;
      return;
    }

    // 기존 마커와 diff
    const prevMarkers = clusterMarkersRef.current;

    // marker 객체는 직접 비교가 어렵기 때문에 recorderId로 매핑 필요
    const getKey = (marker: CustomMarker) =>
      (marker as CustomMarker).data?.recorderIds;

    const prevMap = new Map(prevMarkers.map((m) => [getKey(m), m]));
    const nextMap = new Map(newMarkers.map((m) => [getKey(m), m]));

    const toAdd: CustomMarker[] = [];
    const toRemove: CustomMarker[] = [];
    // 새로 추가해야 할 마커들
    newMarkers.forEach((m) => {
      if (!prevMap.has(getKey(m))) {
        toAdd.push(m);
      }
    });
    // 제거해야 할 마커들
    prevMarkers.forEach((m) => {
      if (!nextMap.has(getKey(m))) {
        toRemove.push(m);
      }
    });

    clusterer.removeMarkers(toRemove);
    clusterer.addMarkers(toAdd);

    clusterMarkersRef.current = newMarkers;
  }, [
    map,
    clusterRadius,
    clusterRenderer,
    defaultOnClusterClickHandler,
    markers,
    Object.values(markerMapRef.current),
  ]);

  useEffect(() => {
    const prevAnimated = new Set(prevAnimatedRef.current);

    const nextAnimated = new Set(animatedRecorders);
    let hasDuplicates = false;
    const seen = new Set<string>();

    const added: string[] = [];
    const removed: string[] = [];

    animatedRecorders.forEach((id) => {
      if (seen.has(id)) {
        hasDuplicates = true;
      }
      seen.add(id);
      if (!prevAnimated.has(id) || hasDuplicates) {
        added.push(id);
      }
    });

    prevAnimatedRef.current.forEach((id) => {
      const isStillIncluded = Object.keys(markerMapRef.current).some((key) => {
        const keyIds = key.split(":");
        return keyIds.includes(id) && keyIds.some((k) => nextAnimated.has(k));
      });
      if (!isStillIncluded) {
        removed.push(id);
      }
    });

    const applyAnimationToMarker = (
      ids: string[],
      action: "add" | "remove"
    ) => {
      ids.forEach((id) => {
        const matchedMarkers: CustomMarker[] = Object.entries(
          markerMapRef.current
        )
          .filter(([key]) => key.split(":").includes(id))
          .map(([_, marker]) => marker);

        matchedMarkers.forEach((marker: CustomMarker) => {
          if (!(marker?.content instanceof HTMLElement)) return;

          const circle = marker.content.querySelector(".marker-circle-bg");

          if (circle) {
            circle.classList.toggle("circle-animation", action === "add");
          }

          if (action === "add" && marker.position) {
            map?.panTo(marker.position);
          }
        });
      });
    };

    applyAnimationToMarker(added, "add");
    applyAnimationToMarker(removed, "remove");

    // clusterer 추가
    const clusterEls = document.querySelectorAll("[data-cluster-id]");

    clusterEls.forEach((el) => {
      const clusterIds = el.getAttribute("data-cluster-id")?.split(":") ?? [];
      const isAnimated = clusterIds.some((id) => nextAnimated.has(id));
      const circle = el.querySelector(".clusterer-circle-bg");

      if (circle) {
        circle.classList.toggle("circle-animation", isAnimated);
      }
    });

    // 업데이트
    prevAnimatedRef.current = animatedRecorders;
  }, [animatedRecorders]);

  const onZoomFit = () => {
    if (!map || Object.keys(markerMapRef.current).length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    if (Object.values(markerMapRef.current).length === 0) {
      map.setZoom(5);
    } else {
      Object.values(markerMapRef.current).forEach((marker) => {
        if (marker.position) {
          bounds.extend(marker.position);
        }
      });
      map.fitBounds(bounds);
    }
  };

  const onZoomPlus = () => {
    if (!map) return;
    const currentZoom = map.getZoom() ?? 10;
    if (currentZoom < 19) {
      map.setZoom(currentZoom + 1);
    }
  };

  const onZoomOut = () => {
    if (!map) return;
    const currentZoom = map.getZoom() ?? 10;

    if (currentZoom > 4) {
      map.setZoom(currentZoom - 1);
    }
  };

  const closeInfoWindow = useCallback(() => {
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      google.maps.event.clearInstanceListeners(infoWindowRef.current);
      infoWindowRef.current = null;
    }
  }, [infoWindowRef.current]);

  return {
    markerRef: markerMapRef,
    markerClusterRef: clustererRef,
    infoWindowRef,
    infoAnchorRef,
    onZoomFit,
    onZoomPlus,
    onZoomOut,
    closeInfoWindow,
  };
}
